import { isValidUrl } from "./url";
import isValidEmail from "./email";

/**
 * Team link URL validation (shared):
 *
 * - {@link validateUrlOrEmail} — core rules (http(s)/ftp URL, plain email, mailto:).
 * - {@link getTeamLinkUrlBlurValidationError} — team **Details** edit + **Change details** modal:
 *   validate on blur; **empty is OK** (row can be cleared mid-edit).
 * - {@link getTeamLinkValueValidationError} — **TeamAttributeLinksList** (org chart details, etc.):
 *   validate on every change; **empty is an error** (filled link row must have a value).
 */

/**
 * Validates a URL or email address
 * @param {string} url - URL or email to validate
 * @returns {{ isValid: boolean, error: string|null }} Validation result
 *
 * Empty or whitespace-only input returns isValid: true (no validation error).
 * This allows optional fields to be left empty; required-field checks should
 * be done separately by the caller.
 */
export const validateUrlOrEmail = (url) => {
  if (!url || url.trim() === "") {
    return { isValid: true, error: null };
  }

  const trimmedUrl = url.trim();

  // Allow mailto: links
  if (trimmedUrl.toLowerCase().startsWith("mailto:")) {
    const emailPart = trimmedUrl.substring(7).split("?")[0].trim();
    if (isValidEmail(emailPart)) {
      return { isValid: true, error: null };
    }
    return {
      isValid: false,
      error: "Please enter a valid email address after mailto:",
    };
  }

  // Allow plain email addresses (will be converted to mailto:)
  if (isValidEmail(trimmedUrl)) {
    return { isValid: true, error: null };
  }

  // Validate as URL
  if (isValidUrl(trimmedUrl)) {
    return { isValid: true, error: null };
  }

  return {
    isValid: false,
    error: "Please enter a valid URL or email address",
  };
};

/**
 * Link URL field on blur: {@link TeamEdit}, {@link TeamDescriptionEditModal}.
 * Whitespace-only is treated as empty → no error (same as {@link validateUrlOrEmail}).
 * @param {string|undefined} value
 * @returns {string|null} Error message or null when valid
 */
export const getTeamLinkUrlBlurValidationError = (value) =>
  validateUrlOrEmail(value ?? "").error;

/**
 * Required-field validation for team link `value` (org chart list, etc.).
 * Empty is invalid; non-empty uses {@link validateUrlOrEmail}.
 * @param {string|undefined} value
 * @returns {string|null} Error message or null when valid
 */
export const getTeamLinkValueValidationError = (value) => {
  const trimmed = (value ?? "").trim();
  if (!trimmed) {
    return "Please enter a valid URL";
  }
  const { isValid, error } = validateUrlOrEmail(trimmed);
  if (!isValid) {
    return error || "Please enter a valid URL or email address";
  }
  return null;
};

/**
 * Normalizes email addresses to mailto: format
 * @param {string} href - URL or email address
 * @returns {string} Normalized href (email addresses converted to mailto:)
 */
export const normalizeEmailToMailto = (href) => {
  if (!href) {
    return href;
  }

  const trimmedHref = href.trim();

  // If it's already mailto:, return as is
  if (trimmedHref.toLowerCase().startsWith("mailto:")) {
    return trimmedHref;
  }

  // If it's a plain email, convert to mailto:
  if (isValidEmail(trimmedHref)) {
    return `mailto:${trimmedHref}`;
  }

  return trimmedHref;
};
